import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { PageNameResponse } from '@services/associations/model/pageNameResponse';
import { PhoneNumberDto } from '@services/authentication/model/phoneNumberDto';
import { PaginationModel } from '@shared/models/pagination.model';
import { Observable } from 'rxjs';
import {ManagerSearchRequest} from "@services/back-office/model/managerSearchRequest";
import {PageManagerResponse} from "@services/back-office/model/pageManagerResponse";
import {ManagerSimpleResponse} from "@services/back-office/model/managerSimpleResponse";
import {ManagerResponse} from "@services/back-office/model/managerResponse";
import {ManagerAssignRequest} from "@services/back-office/model/managerAssignRequest";
import {UserListSearchRequest} from "@services/back-office/model/userListSearchRequest";
import {PageUserListModel} from "@services/back-office/model/pageUserListModel";
import {UserInfoModel} from "@services/back-office/model/userInfoModel";
import {RoleModel} from "@services/back-office/model/roleModel";

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private baseUrl = environment.baseUrl;

  constructor(
    private http: HttpClient,
  ) { }

  /** Get PhoneBook */
  getPhonebook(): Observable<ManagerSimpleResponse[]> {
    const url = `${this.baseUrl}/back-office/v1/manager/list/phone/book`;
    return this.http.get<ManagerSimpleResponse[]>(url);
  }

  /** Find User */
  findUser(request: PhoneNumberDto): Observable<UserInfoModel> {
    const url = `${this.baseUrl}/back-office/v1/users/info/login/${request.phoneNumber}`;
    return this.http.get<UserInfoModel>(url);
  }

  /** Get Roles */
  getRoles(): Observable<RoleModel[]> {
    const url = `${this.baseUrl}/back-office/v1/manager/role/list`;
    return this.http.get<RoleModel[]>(url);
  }

  getRole(roleId: string): Observable<RoleModel> {
    const url = `${this.baseUrl}/back-office/v1/manager/role/${roleId}`;
    return this.http.get<RoleModel>(url);
  }

  addRole(request: RoleModel): Observable<RoleModel> {
    const url = `${this.baseUrl}/back-office/v1/manager/role`;
    return this.http.post<RoleModel>(url, request);
  }

  editRole(roleId: string, request: RoleModel): Observable<RoleModel> {
    const url = `${this.baseUrl}/back-office/v1/manager/role`;
    return this.http.put<RoleModel>(url, request);
  }

  deleteRole(roleId: string): Observable<void> {
    const url = `${this.baseUrl}/back-office/v1/manager/role/${roleId}`;
    return this.http.delete<void>(url);
  }

  /** Get Administrators Roles */
  getAdministratorsRoles(): Observable<RoleModel[]> {
    const url = `${this.baseUrl}/back-office/v1/manager/role/list`;
    return this.http.get<RoleModel[]>(url);
  }

  /** Search Managers For Autocomplete */
  searchManagers(request: ManagerSearchRequest, recent?: boolean, pagination?: PaginationModel): Observable<PageNameResponse> {
    const url = `${this.baseUrl}/back-office/v1/manager/list/simple?page=${pagination ? pagination.number : ''}&size=${pagination ? pagination.size : '100'}&recent=${recent ? 'true' : 'false'}`;
    return this.http.post<PageNameResponse>(url, request);
  }

  /** Search Managers For Autocomplete */
  loadManagers(request: ManagerSearchRequest, pagination?: PaginationModel): Observable<PageManagerResponse> {
    const url = `${this.baseUrl}/back-office/v1/manager/list?page=${pagination ? pagination.number : ''}&size=${pagination ? pagination.size : '50'}`;
    return this.http.post<PageManagerResponse>(url, request);
  }

  /** Add Administrator */
  addAdministrator(userId: string, roleId: string, managerData: ManagerAssignRequest): Observable<ManagerResponse> {
    const url = `${this.baseUrl}/back-office/v1/manager/assign/${userId}/${roleId}`;
    return this.http.post<ManagerResponse>(url, managerData);
  }

  /** Delete manager */
  deleteAdministrator(userId: string): Observable<void> {
    const url = `${this.baseUrl}/back-office/v1/manager/${userId}`;
    return this.http.delete<void>(url);
  }

  /** Get Users */
  getUsers(request: UserListSearchRequest, pagination: PaginationModel): Observable<PageUserListModel> {
    const url = `${this.baseUrl}/back-office/v1/users/search?page=${pagination.number}&size=${pagination.size}`;
    return this.http.post<PageUserListModel>(url, request);
  }

  getUserInfo(userId: string): Observable<UserInfoModel> {
    const url = `${this.baseUrl}/back-office/v1/users/info/id/${userId}`;
    return this.http.get<UserInfoModel>(url);
  }

  /** Change User's Status */
  changeUsersStatus(userId: string): Observable<void> {
    const url = `${this.baseUrl}/back-office/v1/users/block/${userId}`;
    return this.http.get<void>(url);
  }
}
